import React, {useEffect, useMemo, useState} from 'react'
import {
  exportRequisitions,
  getAllInventoryCategoriesForAllCompanies,
  getCompanyGroupAdmins,
  getInventoryItemSubCategories,
  getRequisitionExportCount,
  getSubCompanyAdmins,
  getVesselsByCompany,
  RequisitionExportFilters,
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  InventoryCategory,
  InventoryItemSubCategory,
  SubCompanyAdmin,
  Vessel,
} from '../core/_models'

type RequisitionExportModalProps = {
  open: boolean
  onClose: () => void
  initialFilters: {
    companyGroupAdminId?: number | null
    companyAdminId?: number | null
    vesselId?: number | null
    requisitionCode?: string | null
    categoryName?: string | null
    search?: string | null
    status?: string | null
    dateFrom?: string | null
    dateTo?: string | null
    selectedColumns?: string[] | null
  }
  currentUser: any
  requisitionScopeAll: boolean
  requisitionScopeCompany: boolean
  requisitionScopeVessel: boolean
}

type ExportFormState = RequisitionExportFilters

type ExportColumnOption = {
  key: string
  label: string
}

const STATUS_OPTIONS = [
  'DRAFT',
  'SUBMITTED',
  'TO_BE_REVISED',
  'VESSEL_APPROVED',
  'SHORE_APPROVED',
  'RFQ_ISSUED',
  'RFQ_REWORK_PENDING',
  'QUOTATION_RECEIVED',
  'PO_CREATED',
  'PARTIALLY_COMPLETED',
  'FULFILLED',
  'REJECTED',
  'CANCELLED',
]

const EXPORT_COLUMN_OPTIONS: ExportColumnOption[] = [
  {key: 'REQUISITION_CODE', label: 'Requisition Code'},
  {key: 'VESSEL', label: 'Vessel'},
  {key: 'COMPANY', label: 'Company / Company Group'},
  {key: 'STATUS', label: 'Status'},
  {key: 'REQUEST_DATE', label: 'Request Date'},
  {key: 'REQUESTED_BY', label: 'Requested By'},
  {key: 'ITEM_DESCRIPTION', label: 'Item Name / Description'},
  {key: 'CATEGORY', label: 'Category'},
  {key: 'SUBCATEGORY', label: 'Subcategory'},
  {key: 'QUANTITY', label: 'Quantity'},
  {key: 'UOM', label: 'UOM'},
  {key: 'ACCOUNT_CODE', label: 'Account Code'},
  {key: 'BUDGET_CODE', label: 'Budget Code'},
  {key: 'REMARKS', label: 'Remarks / Notes'},
  {key: 'CREATED_AT', label: 'Created At'},
  {key: 'UPDATED_AT', label: 'Updated At'},
]

const DEFAULT_EXPORT_COLUMN_KEYS = EXPORT_COLUMN_OPTIONS.map((option) => option.key)
const NUMERIC_EXPORT_FIELDS: Array<keyof ExportFormState> = [
  'companyGroupAdminId',
  'companyAdminId',
  'vesselId',
  'requisitionId',
  'categoryId',
  'subcategoryId',
]

const resolveCurrentCompanyGroupId = (currentUser: any): number | null =>
  Number(
    currentUser?.vessel?.companyGroupAdminId ??
      currentUser?.vessel?.companyGroupAdmin?.id ??
      currentUser?.companyGroupAdminId ??
      currentUser?.companyGroupAdmin?.id ??
      currentUser?.companyAdmin?.cgaid?.id ??
      currentUser?.cgaid?.id ??
      currentUser?.cgaid ??
      0
  ) || null

const resolveCurrentVesselId = (currentUser: any): number | null =>
  Number(currentUser?.vessel?.id ?? currentUser?.vesselId ?? 0) || null

const resolveCompanyUsername = (currentUser: any): string =>
  String(
    currentUser?.companyGroupAdmin?.username ??
      currentUser?.vessel?.companyGroupAdmin?.username ??
      ''
  ).trim()

const normalizeSelectedColumns = (selectedColumns?: string[] | null): string[] => {
  const nextColumns = Array.isArray(selectedColumns)
    ? selectedColumns
        .map((value) => String(value || '').trim())
        .filter((value) => DEFAULT_EXPORT_COLUMN_KEYS.includes(value))
    : []
  return nextColumns.length > 0 ? Array.from(new Set(nextColumns)) : [...DEFAULT_EXPORT_COLUMN_KEYS]
}

const buildDefaultForm = (
  initialFilters: RequisitionExportModalProps['initialFilters'],
  currentUser: any,
  requisitionScopeCompany: boolean,
  requisitionScopeVessel: boolean
): ExportFormState => ({
  format: 'EXCEL',
  companyGroupAdminId:
    requisitionScopeCompany || requisitionScopeVessel
      ? resolveCurrentCompanyGroupId(currentUser)
      : initialFilters.companyGroupAdminId ?? null,
  companyAdminId: initialFilters.companyAdminId ?? null,
  vesselId: requisitionScopeVessel ? resolveCurrentVesselId(currentUser) : initialFilters.vesselId ?? null,
  requisitionCode: initialFilters.requisitionCode || null,
  status: initialFilters.status || null,
  categoryId: null,
  subcategoryId: null,
  dateFrom: initialFilters.dateFrom || null,
  dateTo: initialFilters.dateTo || null,
  search: initialFilters.search || null,
  selectedColumns: normalizeSelectedColumns(initialFilters.selectedColumns),
})

export const RequisitionExportModal: React.FC<RequisitionExportModalProps> = ({
  open,
  onClose,
  initialFilters,
  currentUser,
  requisitionScopeAll,
  requisitionScopeCompany,
  requisitionScopeVessel,
}) => {
  const [form, setForm] = useState<ExportFormState>(() =>
    buildDefaultForm(initialFilters, currentUser, requisitionScopeCompany, requisitionScopeVessel)
  )
  const [companyGroups, setCompanyGroups] = useState<CompanyGroupAdmin[]>([])
  const [companies, setCompanies] = useState<SubCompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [subcategories, setSubcategories] = useState<InventoryItemSubCategory[]>([])
  const [count, setCount] = useState<number | null>(null)
  const [loadingLookups, setLoadingLookups] = useState(false)
  const [loadingCount, setLoadingCount] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedCompanyGroup = useMemo(
    () => companyGroups.find((item) => item.id === form.companyGroupAdminId) ?? null,
    [companyGroups, form.companyGroupAdminId]
  )
  const selectedColumnCount = form.selectedColumns?.length ?? 0

  useEffect(() => {
    if (!open) return
    setForm(buildDefaultForm(initialFilters, currentUser, requisitionScopeCompany, requisitionScopeVessel))
    setError(null)
    setCount(null)
  }, [open, initialFilters, currentUser, requisitionScopeCompany, requisitionScopeVessel])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadLookups = async () => {
      setLoadingLookups(true)
      try {
        const [categoryRows, subcategoryRows] = await Promise.all([
          getAllInventoryCategoriesForAllCompanies(),
          getInventoryItemSubCategories(true),
        ])
        if (cancelled) return
        setCategories(categoryRows || [])
        setSubcategories(subcategoryRows || [])

        if (requisitionScopeAll) {
          const groups = await getCompanyGroupAdmins()
          if (cancelled) return
          setCompanyGroups(groups || [])
        } else {
          setCompanyGroups([])
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load export filters')
      } finally {
        if (!cancelled) setLoadingLookups(false)
      }
    }
    void loadLookups()
    return () => {
      cancelled = true
    }
  }, [open, requisitionScopeAll])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadCompanyScopedLookups = async () => {
      const companyGroupId =
        requisitionScopeCompany || requisitionScopeVessel
          ? resolveCurrentCompanyGroupId(currentUser)
          : form.companyGroupAdminId
      if (!companyGroupId) {
        setCompanies([])
        setVessels([])
        return
      }
      try {
        const companyUsername =
          requisitionScopeCompany || requisitionScopeVessel
            ? resolveCompanyUsername(currentUser)
            : String(selectedCompanyGroup?.username || '').trim()
        const [companyRows, vesselRows] = await Promise.all([
          getSubCompanyAdmins(companyGroupId),
          companyUsername ? getVesselsByCompany(companyUsername) : Promise.resolve([] as Vessel[]),
        ])
        if (cancelled) return
        setCompanies(companyRows || [])
        setVessels(vesselRows || [])
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load company or vessel filters')
      }
    }
    void loadCompanyScopedLookups()
    return () => {
      cancelled = true
    }
  }, [
    open,
    currentUser,
    form.companyGroupAdminId,
    requisitionScopeCompany,
    requisitionScopeVessel,
    selectedCompanyGroup,
  ])

  useEffect(() => {
    if (!open || categories.length === 0) return
    if (form.categoryId) return
    const categoryName = String(initialFilters.categoryName || '').trim().toLowerCase()
    if (!categoryName) return
    const matched = categories.find((category) => category.name?.trim().toLowerCase() === categoryName)
    if (matched) {
      setForm((prev) => ({...prev, categoryId: matched.id}))
    }
  }, [open, categories, form.categoryId, initialFilters.categoryName])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadCount = async () => {
      setLoadingCount(true)
      try {
        const nextCount = await getRequisitionExportCount(form)
        if (!cancelled) setCount(nextCount)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to preview export count')
      } finally {
        if (!cancelled) setLoadingCount(false)
      }
    }
    void loadCount()
    return () => {
      cancelled = true
    }
  }, [open, form])

  const handleFieldChange = (name: keyof ExportFormState, value: string) => {
    const nextValue = value === '' ? null : value
    setForm((prev) => ({
      ...prev,
      [name]: NUMERIC_EXPORT_FIELDS.includes(name)
        ? nextValue === null
          ? null
          : Number(nextValue)
        : nextValue,
      ...(name === 'companyGroupAdminId'
        ? {companyAdminId: null, vesselId: null}
        : name === 'categoryId'
        ? {subcategoryId: null}
        : {}),
    }))
  }

  const handleToggleSelectedColumn = (columnKey: string) => {
    setForm((prev) => {
      const currentColumns = prev.selectedColumns || []
      const nextColumns = currentColumns.includes(columnKey)
        ? currentColumns.filter((value) => value !== columnKey)
        : [...currentColumns, columnKey]
      return {...prev, selectedColumns: nextColumns}
    })
  }

  const handleSelectAllFields = () => {
    setForm((prev) => ({...prev, selectedColumns: [...DEFAULT_EXPORT_COLUMN_KEYS]}))
  }

  const handleClearFields = () => {
    setForm((prev) => ({...prev, selectedColumns: []}))
  }

  const handleClear = () => {
    const defaults = buildDefaultForm(initialFilters, currentUser, requisitionScopeCompany, requisitionScopeVessel)
    setForm((prev) => ({
      ...defaults,
      selectedColumns:
        prev.selectedColumns && prev.selectedColumns.length > 0
          ? prev.selectedColumns
          : [...DEFAULT_EXPORT_COLUMN_KEYS],
    }))
    setError(null)
  }

  const handleExport = async () => {
    if (!form.selectedColumns || form.selectedColumns.length === 0) {
      setError('Select at least one field to export')
      return
    }
    setExporting(true)
    setError(null)
    try {
      await exportRequisitions(form)
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  if (!open) return null

  return (
    <div className='modal d-block' style={{backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2050}}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title text-dark'>Export Requisitions</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={onClose} disabled={exporting} />
          </div>
          <div className='modal-body'>
            <div className='row g-3'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold text-dark'>Format</label>
                <select
                  className='form-select'
                  value={form.format}
                  onChange={(e) => handleFieldChange('format', e.target.value)}
                  disabled={exporting}
                >
                  <option value='EXCEL'>Excel</option>
                  <option value='PDF'>PDF</option>
                </select>
              </div>
              {(requisitionScopeAll || requisitionScopeCompany) && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Company Group</label>
                  <select
                    className='form-select'
                    value={form.companyGroupAdminId ?? ''}
                    onChange={(e) => handleFieldChange('companyGroupAdminId', e.target.value)}
                    disabled={exporting || requisitionScopeCompany}
                  >
                    <option value=''>All</option>
                    {(requisitionScopeCompany
                      ? [{id: resolveCurrentCompanyGroupId(currentUser), name: currentUser?.companyGroupAdmin?.name || currentUser?.vessel?.companyGroupAdmin?.name || 'Current Company Group'}]
                      : companyGroups
                    ).map((group: any) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(requisitionScopeAll || requisitionScopeCompany) && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Company</label>
                  <select
                    className='form-select'
                    value={form.companyAdminId ?? ''}
                    onChange={(e) => handleFieldChange('companyAdminId', e.target.value)}
                    disabled={exporting || loadingLookups || companies.length === 0}
                  >
                    <option value=''>All</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className='col-md-3'>
                <label className='form-label fw-semibold text-dark'>Vessel</label>
                <select
                  className='form-select'
                  value={form.vesselId ?? ''}
                  onChange={(e) => handleFieldChange('vesselId', e.target.value)}
                  disabled={exporting || requisitionScopeVessel || vessels.length === 0}
                >
                  <option value=''>All</option>
                  {vessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.fleet_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-dark'>Requisition Code</label>
                <input
                  className='form-control'
                  value={form.requisitionCode ?? ''}
                  onChange={(e) => handleFieldChange('requisitionCode', e.target.value)}
                  disabled={exporting}
                />
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-dark'>Search</label>
                <input
                  className='form-control'
                  value={form.search ?? ''}
                  onChange={(e) => handleFieldChange('search', e.target.value)}
                  disabled={exporting}
                />
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-dark'>Status</label>
                <select
                  className='form-select'
                  value={form.status ?? ''}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  disabled={exporting}
                >
                  <option value=''>All</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-dark'>Category</label>
                <select
                  className='form-select'
                  value={form.categoryId ?? ''}
                  onChange={(e) => handleFieldChange('categoryId', e.target.value)}
                  disabled={exporting || loadingLookups}
                >
                  <option value=''>All</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-dark'>Subcategory</label>
                <select
                  className='form-select'
                  value={form.subcategoryId ?? ''}
                  onChange={(e) => handleFieldChange('subcategoryId', e.target.value)}
                  disabled={exporting || loadingLookups}
                >
                  <option value=''>All</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold text-dark'>Date From</label>
                <input
                  type='date'
                  className='form-control'
                  value={form.dateFrom ?? ''}
                  onChange={(e) => handleFieldChange('dateFrom', e.target.value)}
                  disabled={exporting}
                />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold text-dark'>Date To</label>
                <input
                  type='date'
                  className='form-control'
                  value={form.dateTo ?? ''}
                  onChange={(e) => handleFieldChange('dateTo', e.target.value)}
                  disabled={exporting}
                />
              </div>
            </div>

            <div className='border rounded p-3 mt-4'>
              <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3'>
                <div>
                  <div className='fw-semibold text-dark'>Select fields to export</div>
                  <div className='text-muted small'>{selectedColumnCount} field(s) selected</div>
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  <button type='button' className='btn btn-sm btn-light-primary' onClick={handleSelectAllFields} disabled={exporting}>
                    Select All Fields
                  </button>
                  <button type='button' className='btn btn-sm btn-light' onClick={handleClearFields} disabled={exporting}>
                    Clear Fields
                  </button>
                </div>
              </div>
              <div className='row g-2'>
                {EXPORT_COLUMN_OPTIONS.map((option) => {
                  const checked = form.selectedColumns?.includes(option.key) ?? false
                  return (
                    <div className='col-md-4' key={option.key}>
                      <label className='form-check form-check-custom form-check-solid align-items-start gap-2 border rounded px-3 py-2 w-100'>
                        <input
                          className='form-check-input mt-1'
                          type='checkbox'
                          checked={checked}
                          onChange={() => handleToggleSelectedColumn(option.key)}
                          disabled={exporting}
                        />
                        <span className='form-check-label fw-medium text-gray-800'>{option.label}</span>
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='alert alert-light border mt-4 mb-0'>
              {loadingCount ? (
                <span>Calculating export preview...</span>
              ) : (
                <span>
                  <strong>{count ?? 0}</strong> requisition(s) match the current export filters.
                </span>
              )}
            </div>
            {error ? <div className='alert alert-danger mt-3 mb-0'>{error}</div> : null}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={handleClear} disabled={exporting}>
              Clear Filters
            </button>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={exporting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleExport}
              disabled={exporting || loadingLookups || loadingCount || !form.selectedColumns || form.selectedColumns.length === 0}
            >
              {exporting ? 'Exporting...' : `Export ${form.format === 'PDF' ? 'PDF' : 'Excel'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RequisitionExportModal
